import { LayoutContainer } from 'components/Layout';
import RichText from 'components/RichText';
import Text from 'components/Text';
import { useIsEditing } from 'hooks';
import type {
	ColorField,
	Field,
	MessageSeverity,
	SitecoreBlockProps,
} from 'models/sitecore';
import type { HTMLString } from 'types';
import { getEditorMargin } from 'utils/business-logic';
import { cn } from 'utils/classNames';

const bgForSeverity: Record<MessageSeverity, string> = {
	'': '',
	'Error': 'bg-error',
	'Info': 'bg-information',
	'Warning': 'bg-warning',
};

interface Props extends SitecoreBlockProps {
	fields?: {
		backgroundColor?: ColorField;
		content: Field<HTMLString>;
		heading: Field<string>;
		severity?: Field<MessageSeverity>;
	};
}

/**
 * A simple Content Block component, with a heading and rich text block.
 * This is the most basic building block of a content site, and the most basic
 * JSS component that's useful.
 */
export default function ContentBlock({ fields, params }: Props) {
	const isEditing = useIsEditing();

	if (
		!fields ||
		(!isEditing && !fields.heading.value && !fields.content.value)
	) {
		return null;
	}

	return (
		<LayoutContainer
			id={params?.anchor}
			outerClassName={getEditorMargin(params)}
			// Kept to not break usages where it's a full page color band that
			// will be replaced by coming hero(?) block. Let severity win if set
			// just to not end up with both active.
			backgroundColor={
				fields.severity?.value
					? undefined
					: fields.backgroundColor?.fields?.colorHex?.value
			}
		>
			<div
				className={cn(
					'relative max-w-col-6',
					fields.severity?.value && [
						bgForSeverity[fields.severity.value],
						'rounded-lg p-r-lg forced-colors:border',
					],
				)}
			>
				{fields.heading && (
					<Text
						className="mb-r-xs"
						as={params?.heading || 'h2'}
						field={fields.heading}
					/>
				)}
				<RichText field={fields.content} inheritStyle />
			</div>
		</LayoutContainer>
	);
}
ContentBlock.displayName = 'ContentBlock';
